// Presentational parts of Settings > Deployment (formerly "USB Installer"; no WebUSB, no fetch) - rendered by UsbInstaller.tsx
// and unit-tested with react-dom/server. Identifiers are masked unless `reveal` is set.
import { mask, type RadioIdentity } from '../../usb/identity';
import type { InstalledPackage, InstallPhase, ProvisioningRecord, Resolution } from '../../usb/workflow';
import type { UsbEvidence } from '../../services/usbInstaller';

const show = (v: string, reveal: boolean) => (v ? (reveal ? v : mask(v)) : '—');

function Section({ title, children, tone }: { title: string; children: React.ReactNode; tone?: 'ok' | 'bad' | 'warn' }) {
  const border = tone === 'bad' ? 'border-red-600' : tone === 'warn' ? 'border-yellow-600' : tone === 'ok' ? 'border-green-700' : 'border-gray-700';
  return (
    <section className={`bg-gray-800 border ${border} rounded-lg p-4`}>
      <h3 className="text-sm font-semibold text-gray-300 mb-2 uppercase tracking-wide">{title}</h3>
      {children}
    </section>
  );
}

function Rows({ rows }: { rows: [string, React.ReactNode][] }) {
  return (
    <dl className="grid grid-cols-[10rem_1fr] gap-x-3 gap-y-1 text-sm">
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-gray-400">{k}</dt>
          <dd className="text-white font-mono break-all">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Requirements() {
  return (
    <Section title="Requirements">
      <p className="text-sm text-gray-300 mb-2">Connect an Android radio by USB (a factory-new radio for installation).</p>
      <ul className="text-sm text-gray-300 list-disc pl-5 space-y-1">
        <li>Microsoft Edge or Google Chrome</li>
        <li>USB debugging enabled on the radio</li>
        <li>Authorize USB debugging on the radio if prompted</li>
        <li>Native adb / Android Studio must not own the device (close Android Studio, run <code>adb kill-server</code>)</li>
      </ul>
    </Section>
  );
}

export function IdentityPanels({ identity, pkg, reveal }: { identity: RadioIdentity; pkg: InstalledPackage; reveal: boolean }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-usb-identity>
      <Section title="Device">
        <Rows rows={[
          ['Manufacturer', identity.manufacturer || '—'],
          ['Brand', identity.brand || '—'],
          ['Model', identity.model || '—'],
          ['Android', identity.androidRelease ? `${identity.androidRelease} (SDK ${identity.sdkInt})` : '—'],
          ['Serial', identity.serial || '—'],
          ...identity.imeis.map((v, i) => [`IMEI ${i + 1}`, show(v, reveal)] as [string, string]),
        ]} />
      </Section>
      <Section title="SIM">
        {identity.sims.length === 0 ? <p className="text-sm text-gray-400">No SIM detected</p> : identity.sims.map((s) => (
          <Rows key={s.slot} rows={[
            [`Slot ${s.slot}`, 'Present'],
            ['ICCID', show(s.iccid, reveal)],
            ['IMSI', show(s.imsi, reveal)],
            ['Operator', [s.operatorName, s.operatorNumeric && `${s.operatorNumeric.slice(0, 3)}/${s.operatorNumeric.slice(3)}`].filter(Boolean).join(' · ') || '—'],
          ]} />
        ))}
      </Section>
      <Section title="System">
        <Rows rows={[
          ['PTT-Radio installed', pkg.installed ? 'YES' : 'NO'],
          ...(pkg.installed ? [['Installed version', `${pkg.versionName} (${pkg.versionCode})`] as [string, string]] : []),
        ]} />
      </Section>
    </div>
  );
}

export function ResolutionPanels({ res, pkg }: { res: Resolution; pkg: InstalledPackage }) {
  if (res.status === 'UNKNOWN_DEVICE' || res.status === 'MODEL_NOT_USB_ENABLED') {
    return (
      <div data-usb-unknown className="bg-red-900/40 border-2 border-red-600 rounded-lg p-4">
        <p className="text-lg font-bold text-red-300">{res.status === 'UNKNOWN_DEVICE' ? 'UNKNOWN DEVICE' : 'MODEL NOT ENABLED FOR USB INSTALLATION'}</p>
        <p className="text-sm text-red-200 mt-1">{res.reason || 'No matching Model Catalog model exists.'}</p>
        <p className="text-sm text-red-200 mt-1 font-semibold">APK installation is unavailable.</p>
      </div>
    );
  }
  const a = res.artifact;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Section title="Model Catalog" tone={res.model ? 'ok' : 'bad'}>
        <Rows rows={[
          ['Match', res.model ? `${res.model.model_name} (${res.model.manufacturer_code}/${res.model.model_code})` : '—'],
          ['Status', res.status === 'INVALID_IDENTITY' ? 'IDENTITY INCOMPLETE' : 'SUPPORTED'],
        ]} />
        {res.identity_problems && res.identity_problems.length > 0 && (
          <ul className="text-sm text-yellow-300 list-disc pl-5 mt-2">{res.identity_problems.map((p) => <li key={p}>{p}</li>)}</ul>
        )}
      </Section>
      <Section title="APK Repository" tone={a ? 'ok' : 'bad'}>
        {a ? (
          <Rows rows={[
            ['Version', `${a.version_name} (${a.version_code})`],
            ['Target', `${a.target_manufacturer_code}/${a.target_model_code}`],
            ['Trust', a.trust],
            ['Current', a.current ? 'YES' : 'NO'],
            ['SHA-256', `${a.sha256.slice(0, 16)}…`],
            ...(pkg.installed ? [['On the radio', `${pkg.versionName} (${pkg.versionCode})`] as [string, string]] : []),
          ]} />
        ) : <p className="text-sm text-red-300">{res.reason || 'No installable APK.'}</p>}
        {res.existing_enrollment && (
          <p className="text-xs text-gray-400 mt-2">Already enrolled as {res.existing_enrollment.radio_name || 'a radio'} ({res.existing_enrollment.outcome}{res.existing_enrollment.authorized ? ', approved' : ', pending approval'}).</p>
        )}
      </Section>
    </div>
  );
}

const phaseText: Record<InstallPhase, string> = {
  recording: 'Recording the USB provisioning…',
  downloading: 'Downloading the repository APK…',
  transferring: 'Installing on the radio…',
  verifying: 'Verifying the installed package…',
  reporting: 'Recording the result…',
  starting: 'Starting PTT-Radio on the radio…',
};

export function InstallProgress({ phase, progress }: { phase: InstallPhase; progress?: { sent: number; total: number } }) {
  const pct = progress && progress.total > 0 ? Math.floor((progress.sent / progress.total) * 100) : null;
  return (
    <div className="text-sm text-blue-300" data-usb-progress>
      {phaseText[phase]}{phase === 'transferring' && pct !== null ? ` ${pct} %` : ''}
    </div>
  );
}

export const statusLabel: Record<string, string> = {
  READY_TO_INSTALL: 'Ready to install',
  INSTALLING: 'Installing',
  WAITING_FOR_ENROLLMENT: 'Waiting for enrollment',
  MATCHED: 'Matched to enrollment',
  APPROVED: 'Approved',
  FAILED: 'Failed',
  IDENTITY_MISMATCH: 'Identity mismatch',
  CANCELLED: 'Cancelled',
  SUPERSEDED: 'Superseded',
};

export function RecordTable({ records, onCancel }: { records: ProvisioningRecord[]; onCancel?: (id: string) => void }) {
  if (records.length === 0) return <p className="text-sm text-gray-400">No USB provisioning records yet.</p>;
  return (
    <table className="w-full text-sm" data-usb-records>
      <thead className="text-left text-gray-400">
        <tr><th className="py-1">Created</th><th>Model</th><th>Serial</th><th>IMEI</th><th>APK</th><th>Status</th><th /></tr>
      </thead>
      <tbody className="text-gray-200">
        {records.map((r) => (
          <tr key={r.id} className="border-t border-gray-700">
            <td className="py-1">{new Date(r.created_at).toLocaleString()}</td>
            <td>{r.model_name}</td>
            <td className="font-mono">{r.serial_masked}</td>
            <td className="font-mono">{r.imeis_masked.filter(Boolean).join(', ')}</td>
            <td>{r.apk_version_name}</td>
            <td>{statusLabel[r.status] ?? r.status}{r.failure_code ? ` (${r.failure_code})` : ''}</td>
            <td>{onCancel && ['READY_TO_INSTALL', 'INSTALLING', 'WAITING_FOR_ENROLLMENT'].includes(r.status) && (
              <button className="text-xs text-red-300 hover:underline" onClick={() => onCancel(r.id)}>Cancel</button>
            )}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const evidenceText: Record<UsbEvidence['result'], { label: string; cls: string }> = {
  EXACT: { label: 'USB provisioned - identity matches', cls: 'text-green-300 border-green-600' },
  PARTIAL: { label: 'USB provisioned - partial identity match (check manually)', cls: 'text-yellow-300 border-yellow-600' },
  CONFLICT: { label: 'USB provisioning IDENTITY MISMATCH', cls: 'text-red-300 border-red-600' },
  AMBIGUOUS: { label: 'Several USB provisioning records could match (check manually)', cls: 'text-yellow-300 border-yellow-600' },
};

/** Approval evidence: USB installation is evidence, never an approval. */
export function UsbEvidenceBadge({ evidence }: { evidence?: UsbEvidence[] }) {
  if (!evidence || evidence.length === 0) return <p className="text-xs text-gray-500" data-usb-evidence="none">No USB provisioning record</p>;
  return (
    <div className="space-y-1" data-usb-evidence>
      {evidence.map((e) => {
        const t = evidenceText[e.result];
        const fields = Object.entries(e.detail?.fields ?? {}).map(([k, v]) => `${k} ${v.toLowerCase()}`).join(', ');
        return (
          <div key={e.usb_provisioning_id} className={`text-xs border rounded px-2 py-1 ${t.cls}`}>
            <span className="font-semibold">{t.label}</span>
            {' - '}{e.model_name}, {e.apk_version_name}, {new Date(e.provisioned_at).toLocaleString()}{e.network_name ? `, ${e.network_name}` : ''}
            {fields && <span className="text-gray-400"> ({fields})</span>}
            {e.detail?.sim_changed && <span className="text-gray-400"> - SIM changed since USB provisioning</span>}
          </div>
        );
      })}
    </div>
  );
}
